import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  DEFAULT_PAGE_LIMIT,
  OffsetPaginationBar,
} from "@/shared/ui/OffsetPaginationBar";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  BellIcon,
  HistoryIcon,
  InboxIcon,
  ListIcon,
  SendIcon,
  SettingsIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  useEligibleNotificationsQuery,
  useNotificationQueueQuery,
  useWhatsAppWorkerStatusQuery,
} from "../api/queries";
import { DEFAULT_ELIGIBLE_NOTIFICATIONS_FILTERS } from "../lib/eligible-notifications-filters";
import { EligibleNotificationsFilterBar } from "../ui/EligibleNotificationsFilterBar";
import { EligibleNotificationsTable } from "../ui/EligibleNotificationsTable";
import { NotificationQueueTable } from "../ui/NotificationQueueTable";
import { PackageNotificationsSummaryCards } from "../ui/PackageNotificationsSummaryCards";
import { WorkerStatusBanner } from "../ui/WorkerStatusBanner";

export function PackageNotificationsPage() {
  const nav = useNavigate();
  const canSend = useActionPermission("package-notifications:send");
  const canManage = useActionPermission("package-notifications:manage");
  const workerQuery = useWhatsAppWorkerStatusQuery();

  const [filters, setFilters] = useState(DEFAULT_ELIGIBLE_NOTIFICATIONS_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [eligibleOffset, setEligibleOffset] = useState(0);
  const [queueOffset, setQueueOffset] = useState(0);
  const limit = DEFAULT_PAGE_LIMIT;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    setEligibleOffset(0);
  }, [filters.packageType, filters.triggerType, debouncedSearch]);

  const eligibleQuery = useEligibleNotificationsQuery({
    packageType:
      filters.packageType === "all" ? undefined : filters.packageType,
    triggerType:
      filters.triggerType === "all" ? undefined : filters.triggerType,
    search: debouncedSearch || undefined,
    limit,
    offset: eligibleOffset,
  });

  const queueQuery = useNotificationQueueQuery({
    limit,
    offset: queueOffset,
  });

  const eligibleItems = eligibleQuery.data?.items ?? [];
  const eligibleMeta = eligibleQuery.data?.meta;
  const eligibleStats = eligibleQuery.data?.stats;
  const totalAll =
    (eligibleStats?.byPackageType.package ?? 0) +
    (eligibleStats?.byPackageType.private_lesson ?? 0);
  const queueStats = queueQuery.data?.stats;

  const handleFiltersChange = (
    next: typeof DEFAULT_ELIGIBLE_NOTIFICATIONS_FILTERS,
  ) => {
    setFilters(next);
    if (
      next.packageType !== filters.packageType ||
      next.triggerType !== filters.triggerType
    ) {
      setEligibleOffset(0);
    }
  };

  const isSummaryLoading = eligibleQuery.isLoading || queueQuery.isLoading;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແຈ້ງເຕືອນແພັກ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mx-auto w-full max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
                  <BellIcon className="size-6" />
                  ແຈ້ງເຕືອນແພັກ WhatsApp
                </h2>
                <WorkerStatusBanner health={workerQuery.data} />
              </div>
              <p className="text-muted-foreground">
                ລາຍການນັກຮຽນທີ່ແພັກເຫຼືອໜ້ອຍ ຫຼື ໃກ້ໝົດອາຍຸ
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canSend ? (
                <Button
                  onClick={() => nav({ to: "/app/package-notifications/send" })}
                >
                  <SendIcon className="mr-1 size-4" />
                  ສົ່ງ Manual
                </Button>
              ) : null}
              <Button
                variant="outline"
                onClick={() => nav({ to: "/app/package-notifications/logs" })}
              >
                <HistoryIcon className="mr-1 size-4" />
                ປະຫວັດ
              </Button>
              {canManage ? (
                <Button
                  variant="outline"
                  onClick={() =>
                    nav({ to: "/app/settings/package-notifications" })
                  }
                >
                  <SettingsIcon className="mr-1 size-4" />
                  ຕັ້ງຄ່າ
                </Button>
              ) : null}
            </div>
          </div>

          <PackageNotificationsSummaryCards
            classCount={eligibleStats?.byPackageType.package ?? 0}
            privateCount={eligibleStats?.byPackageType.private_lesson ?? 0}
            pending={queueStats?.pending ?? 0}
            failed={queueStats?.failed ?? 0}
            isLoading={isSummaryLoading}
            activePackageFilter={filters.packageType}
            onPackageFilterChange={(packageType) =>
              handleFiltersChange({ ...filters, packageType })
            }
            onQueuePendingClick={() => {
              setQueueOffset(0);
              document
                .getElementById("package-notification-queue")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            onQueueFailedClick={() => {
              setQueueOffset(0);
              document
                .getElementById("package-notification-queue")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          />

          <QueryState
            result={eligibleQuery}
            title="ກຳລັງໂຫຼດລາຍການ"
            description="ກຳລັງດຶງ eligible notifications"
          >
            <Card className="gap-0">
              <CardHeader className="space-y-1 pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ListIcon className="size-4" />
                      ລາຍການ eligible
                    </CardTitle>
                    <CardDescription>
                      ນັກຮຽນທີ່ເຂົ້າເງື່ອນໄຂຕາມການຕັ້ງຄ່າ — ກັ່ນຕົວ ຫຼື
                      ສົ່ງ manual ໄດ້ຈາກປຸ່ມດ້ານເທິງ
                    </CardDescription>
                  </div>
                  {canSend ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        nav({ to: "/app/package-notifications/send" })
                      }
                    >
                      <SendIcon className="mr-1 size-4" />
                      ເລືອກແລ້ວສົ່ງ
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                <EligibleNotificationsFilterBar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  stats={eligibleStats}
                  filteredTotal={eligibleMeta?.total ?? 0}
                  totalAll={totalAll}
                />
                <EligibleNotificationsTable
                  items={eligibleItems}
                  selected={new Set()}
                  onSelectedChange={() => {}}
                  onPreview={() => {}}
                  canSend={false}
                  totalCount={totalAll}
                  offset={eligibleOffset}
                  limit={limit}
                  filteredTotal={eligibleMeta?.total ?? 0}
                  onPaginationChange={setEligibleOffset}
                />
              </CardContent>
            </Card>
          </QueryState>

          <QueryState
            result={queueQuery}
            title="ກຳລັງໂຫຼດຄິວ"
            description="ດຶງ queue"
          >
            <Card id="package-notification-queue" className="gap-0">
              <CardHeader className="space-y-1 pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <InboxIcon className="size-4" />
                  ຄິວສົ່ງ WhatsApp
                </CardTitle>
                <CardDescription>
                  ລາຍການທີ່ enqueue ແລ້ວ — ລໍຖ້າ ຫຼື ກຳລັງປະມວນຜົນ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                <NotificationQueueTable items={queueQuery.data?.items ?? []} />
                <OffsetPaginationBar
                  offset={queueOffset}
                  limit={limit}
                  total={queueQuery.data?.meta.total ?? 0}
                  onChange={setQueueOffset}
                />
              </CardContent>
            </Card>
          </QueryState>
        </div>
      </Main>
    </>
  );
}

export default PackageNotificationsPage;
